import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sandtech-hub-monorepo/ui-kit';
import { PattenUniversityLogo } from './PattenUniversityLogo';

const courseNumberRanges = [
  {
    range: '000-099',
    label: 'Developmental',
    description:
      'Developmental Courses which do not carry credit toward graduation.',
  },
  {
    range: '100-299',
    label: 'Lower Division',
    description:
      'Lower division undergraduate courses which are general and introductory in nature. They are intended to provide a foundation for advance work.',
  },
  {
    range: '300-499',
    label: 'Upper Division',
    description:
      'Upper division undergraduate courses which generally assume prior knowledge or experience in the subject, with content more advanced or specific than lower division courses.',
  },
  {
    range: '500-599',
    label: 'Post-Baccalaureate',
    description: 'Post-baccalaureate and graduate level courses.',
  },
  {
    range: '600-699',
    label: 'Graduate',
    description: 'Graduate Courses.',
  },
];

const undergradGrades = [
  { letter: 'A', score: '90-100%', points: '4.00', achievement: 'Superior' },
  {
    letter: 'B',
    score: '80-89%',
    points: '3.00',
    achievement: 'Above Average',
  },
  { letter: 'C', score: '70-79%', points: '2.00', achievement: 'Average' },
  {
    letter: 'D',
    score: '65-69%',
    points: '1.00',
    achievement: 'Below Average',
  },
  { letter: 'F', score: 'Below 64%', points: '0.00', achievement: 'Failing' },
  { letter: 'F/I', score: 'Fail/Incomplete', points: '0.00', achievement: '-' },
];

const nonGpaGrades = [
  { letter: 'W', meaning: 'Withdrawal' },
  { letter: 'I', meaning: 'Incomplete' },
  {
    letter: 'IP',
    meaning: 'In-Progress: Final grades await course completion',
  },
];

const cardClasses =
  'bg-background border border-border/30 rounded-md p-3 flex flex-col gap-2';

const sectionHeadingClasses = 'text-[11px] font-semibold text-foreground';

const bodyTextClasses = 'text-[9.5px] text-muted-foreground leading-snug';

const codeBadgeClasses =
  'inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded text-[9.5px] font-bold text-muted-foreground';

const OfficialTranscriptInfoPage = () => (
  <div className="bg-background w-full flex flex-col gap-3 print:break-before-page">
    <div className="flex flex-col items-center pt-3 pb-3">
      <PattenUniversityLogo className="h-28 print:h-32" />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:grid-cols-2 print:gap-3">
      {/* Left column */}
      <div className="flex flex-col gap-3">
        {/* Calendar and Credits */}
        <section className={cardClasses}>
          <h3 className={sectionHeadingClasses}>Calendar and Credits</h3>
          <p className={bodyTextClasses}>
            Elmwood Institute utilizes a flexible, overlapping term-based
            structure in its graduate programs. Students enroll in a term at
            the beginning of each month. Each semester is{' '}
            <span className="font-medium text-foreground">16 weeks</span> in
            length.
          </p>
          <p className={bodyTextClasses}>
            Elmwood Institute uses the commonly accepted definition of semester
            credits (also referred to as &ldquo;units&rdquo;) to measure its
            academic courses and programs.
          </p>
        </section>

        {/* Course Numbering System */}
        <section className={cardClasses}>
          <h3 className={sectionHeadingClasses}>Course Numbering System</h3>
          <div className="flex flex-col gap-1.5">
            {courseNumberRanges.map((row) => (
              <div
                key={row.range}
                className="flex items-start gap-2 border border-border/20 rounded p-2"
              >
                <span className={codeBadgeClasses}>{row.range}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[9.5px] font-semibold text-foreground leading-snug">
                    {row.label}
                  </p>
                  <p className={bodyTextClasses}>{row.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Right column */}
      <div className="flex flex-col gap-3">
        {/* Undergraduate Grading System */}
        <section className={cardClasses}>
          <h3 className={sectionHeadingClasses}>Undergraduate Grading System</h3>
          <div className="border border-border/20 rounded overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/20 border-border/20">
                  <TableHead className="text-[8px] uppercase tracking-wider sm:text-[8px] py-1.5 sm:py-1.5 h-auto">
                    Grade
                  </TableHead>
                  <TableHead className="text-[8px] uppercase tracking-wider sm:text-[8px] py-1.5 sm:py-1.5 h-auto">
                    Numerical Score
                  </TableHead>
                  <TableHead className="text-[8px] uppercase tracking-wider sm:text-[8px] py-1.5 sm:py-1.5 h-auto">
                    Grade Points
                  </TableHead>
                  <TableHead className="text-[8px] uppercase tracking-wider sm:text-[8px] py-1.5 sm:py-1.5 h-auto">
                    Achievement Level
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {undergradGrades.map((g) => (
                  <TableRow key={g.letter} className="border-border/20">
                    <TableCell className="py-1.5 sm:py-1.5 text-[9.5px] sm:text-[9.5px]">
                      <span className={codeBadgeClasses}>{g.letter}</span>
                    </TableCell>
                    <TableCell className="py-1.5 sm:py-1.5 text-[9.5px] sm:text-[9.5px] text-foreground">
                      {g.score}
                    </TableCell>
                    <TableCell className="py-1.5 sm:py-1.5 text-[9.5px] sm:text-[9.5px] font-medium text-foreground">
                      {g.points}
                    </TableCell>
                    <TableCell className="py-1.5 sm:py-1.5 text-[9.5px] sm:text-[9.5px] text-muted-foreground">
                      {g.achievement}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className={bodyTextClasses}>
            Undergraduate grades are calculated as a weighted average of
            assignments, quizzes, exams, projects, and coursework per the
            syllabus, then converted to letter grades. The minimum passing
            grade is 65% (D).
          </p>
        </section>

        {/* Non-GPA Affecting Grades */}
        <section className={cardClasses}>
          <h3 className={sectionHeadingClasses}>Non-GPA Affecting Grades</h3>
          <div className="border border-border/20 rounded overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/20 border-border/20">
                  <TableHead className="text-[8px] uppercase tracking-wider sm:text-[8px] py-1.5 sm:py-1.5 h-auto">
                    Code
                  </TableHead>
                  <TableHead className="text-[8px] uppercase tracking-wider sm:text-[8px] py-1.5 sm:py-1.5 h-auto">
                    Meaning
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nonGpaGrades.map((g) => (
                  <TableRow key={g.letter} className="border-border/20">
                    <TableCell className="py-1.5 sm:py-1.5 text-[9.5px] sm:text-[9.5px]">
                      <span className={codeBadgeClasses}>{g.letter}</span>
                    </TableCell>
                    <TableCell className="py-1.5 sm:py-1.5 text-[9.5px] sm:text-[9.5px] text-foreground">
                      {g.meaning}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        {/* Repeat Policy */}
        <section className={cardClasses}>
          <h3 className={sectionHeadingClasses}>Repeat Policy</h3>
          <p className={bodyTextClasses}>
            Students may repeat courses that did not receive a passing grade,
            or to improve overall GPA. All grades, including the grade for the
            repeated course, will appear on the student transcript. Only the
            highest grade achieved is taken into the overall GPA.
          </p>
        </section>
      </div>
    </div>

    {/* Release of Information (full width) */}
    <section className={cardClasses}>
      <h3 className={sectionHeadingClasses}>Release of Information</h3>
      <p className={bodyTextClasses}>
        This information has been forwarded to you are the request of the
        student with the understanding that it will not be released to other
        parties.{' '}
        <span className="font-medium text-foreground">
          The Family Education Rights Act of 1974
        </span>{' '}
        prohibits release of this information to a third party without the
        student&apos;s written consent. The content of this academic transcript
        satisfies the original authorization for release as granted by the
        requestor. Information not included as a part of this record is not
        subject to the original authorization and may only be released upon the
        requestor&apos;s written consent.
      </p>
    </section>
  </div>
);

export default OfficialTranscriptInfoPage;
